import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const SECTION_OPTIONS = [
  { value: 'WORK_VALUE', label: 'Work Done and Bill Value' },
  { value: 'RECOVERIES', label: 'Deductions - Recoveries' },
  { value: 'TOTALS', label: 'Bill Totals' },
];

const emptyForm = () => ({ id: null, label: '', parentKey: 'WORK_VALUE', status: 'ACTIVE' });

const sectionLabel = (code) => SECTION_OPTIONS.find((item) => item.value === code)?.label || code || '—';

const ConfigRaBillMaster = () => {
  const [master, setMaster] = useState(null);
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const res = await adminAPI.getRaBillLineMaster();
    const data = res?.data?.data || res?.data || {};
    setMaster(data);
    setOptions(data.options || []);
  }, []);

  useEffect(() => {
    setLoading(true);
    reload()
      .catch(() => notifyError('Unable to load RA Bill Master'))
      .finally(() => setLoading(false));
  }, [reload]);

  const resetForm = () => setForm(emptyForm());

  const save = async () => {
    if (!String(form.label || '').trim()) {
      notifyError('Name is required');
      return;
    }
    if (!form.parentKey) {
      notifyError('Section is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        parentKey: form.parentKey,
        status: form.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
      };
      if (form.id) await adminAPI.updateRaBillLineOption(form.id, payload);
      else await adminAPI.createRaBillLineOption(payload);
      notifySuccess(form.id ? 'Name updated' : 'Name added');
      resetForm();
      await reload();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to save name');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (option) => {
    setSaving(true);
    try {
      await adminAPI.updateRaBillLineOption(option.id, {
        label: option.label,
        parentKey: option.parentKey,
        status: option.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
        displayOrder: option.displayOrder,
      });
      notifySuccess(option.status === 'ACTIVE' ? 'Name deactivated' : 'Name activated');
      await reload();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to update status');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (option) => {
    if (!(await confirmDialog(`Delete "${option.label}"?`, 'Delete name'))) return;
    try {
      await adminAPI.deleteRaBillLineOption(option.id);
      notifySuccess('Name deleted');
      if (form.id === option.id) resetForm();
      await reload();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to delete name');
    }
  };

  return (
    <div>
      <p className="ce-muted">
        Extra line names used as the Description dropdown when adding a row on Create New RA Bill.
        Pick a section so the name only appears under that table.
      </p>

      <div className="ce-detail" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{form.id ? 'Edit name' : 'Add name'}</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Name <span className="req">*</span></label>
            <input
              value={form.label}
              onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
              placeholder="Price Adjustment"
            />
          </div>
          <div className="form-group">
            <label>Section <span className="req">*</span></label>
            <select
              value={form.parentKey}
              onChange={(e) => setForm((prev) => ({ ...prev, parentKey: e.target.value }))}
            >
              {SECTION_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn-primary btn-sm" disabled={saving} onClick={save}>
            {form.id ? 'Update' : 'Add name'}
          </button>
          {form.id ? (
            <button type="button" className="btn-secondary btn-sm" onClick={resetForm}>Cancel</button>
          ) : null}
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Section</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>Loading...</td></tr>
            ) : options.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>No names yet. Add a line name above.</td></tr>
            ) : options.map((option) => (
              <tr key={option.id}>
                <td>{option.label}</td>
                <td>{sectionLabel(option.parentKey)}</td>
                <td>
                  <span className={`ce-badge ${option.status === 'ACTIVE' ? 'ce-badge--ok' : 'ce-badge--muted'}`}>
                    {option.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => setForm({
                      id: option.id,
                      label: option.label || '',
                      parentKey: option.parentKey || 'WORK_VALUE',
                      status: option.status || 'ACTIVE',
                    })}>
                      Edit
                    </button>
                    <button type="button" className="btn-secondary btn-sm" disabled={saving} onClick={() => toggleStatus(option)}>
                      {option.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => remove(option)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {master?.masterKey ? (
        <p className="ce-muted" style={{ marginTop: 12 }}>Master key: {master.masterKey}</p>
      ) : null}
    </div>
  );
};

export default ConfigRaBillMaster;
